import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getAvatarUrl } from '../../utils/avatarUtils';

// Helper function to escape HTML and prevent XSS
const escapeHtml = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

interface ChatMessage {
  id: string;
  senderId: string;
  senderName?: string;
  senderPhoto?: string;
  message: string;
  timestamp: Date;
  type: 'message' | 'system' | 'time_slot';
}

interface TimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
  proposedBy: string;
  status: 'proposed' | 'accepted' | 'declined';
}

interface ChatStepInterfaceProps {
  connectionId: string;
  currentUserId?: string;
}

export const ChatStepInterface: React.FC<ChatStepInterfaceProps> = ({ 
  connectionId,
  currentUserId = 'user123' 
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [showTimeSlotPicker, setShowTimeSlotPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  useEffect(() => {
    // Listen to messages
    const messagesQuery = query(
      collection(db, 'connectionFlows', connectionId, 'messages'),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ChatMessage));
      setMessages(newMessages);
    });

    // Listen to time slots
    const slotsQuery = query(
      collection(db, 'connectionFlows', connectionId, 'timeSlots'),
      orderBy('startTime', 'asc')
    );
    
    const unsubscribeSlots = onSnapshot(slotsQuery, (snapshot) => {
      const newSlots = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as TimeSlot));
      setTimeSlots(newSlots);
    });

    return () => {
      unsubscribeMessages();
      unsubscribeSlots();
    };
  }, [connectionId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    await addDoc(collection(db, 'connectionFlows', connectionId, 'messages'), {
      senderId: currentUserId,
      message: newMessage,
      timestamp: new Date(),
      type: 'message'
    });

    setNewMessage('');
  };

  const proposeTimeSlot = async () => {
    if (!selectedDate || !selectedTime) return;

    const startTime = new Date(`${selectedDate}T${selectedTime}`);
    const endTime = new Date(startTime.getTime() + (30 * 60 * 1000)); // 30 min default

    await addDoc(collection(db, 'connectionFlows', connectionId, 'timeSlots'), {
      startTime,
      endTime,
      proposedBy: currentUserId,
      status: 'proposed'
    });

    // Send system message
    await addDoc(collection(db, 'connectionFlows', connectionId, 'messages'), {
      senderId: 'system',
      message: `Tijdslot voorgesteld: ${startTime.toLocaleString('nl-NL')}`,
      timestamp: new Date(),
      type: 'system'
    });

    setShowTimeSlotPicker(false);
    setSelectedDate('');
    setSelectedTime('');
  };

  const acceptTimeSlot = async (slotId: string) => {
    await updateDoc(doc(db, 'connectionFlows', connectionId, 'timeSlots', slotId), {
      status: 'accepted'
    });

    // Move to next step automatically
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      currentStep: 'call',
      stepStartTime: new Date(),
      stepDuration: 5,
      scheduledCallTime: timeSlots.find(slot => slot.id === slotId)?.startTime
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-blue-50 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-blue-800 mb-2">Chat Fase</h3>
        <p className="text-blue-600 text-sm">
          Plan samen een telefonische afspraak. Stel tijdsloten voor en accepteer wanneer jullie beiden kunnen.
        </p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 border rounded-lg p-4 overflow-y-auto bg-gray-50 mb-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`mb-3 flex items-end gap-2 ${
              msg.senderId === currentUserId ? 'flex-row-reverse' : 'flex-row'
            }`}
          >
            {/* Avatar */}
            {msg.type !== 'system' && (
              <img
                src={getAvatarUrl(msg.senderPhoto, msg.senderId, msg.senderName, msg.senderId === currentUserId ? '3B82F6' : '4F6660')}
                alt={msg.senderName || 'User'}
                className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                title={msg.senderName || 'Gebruiker'}
              />
            )}
            
            <div className={`flex flex-col ${msg.senderId === currentUserId ? 'items-end' : 'items-start'} ${msg.type === 'system' ? 'flex-1' : ''}`}>
              {/* Sender name (only for other users) */}
              {msg.type !== 'system' && msg.senderId !== currentUserId && msg.senderName && (
                <span className="text-xs text-gray-500 mb-1 px-1">{msg.senderName}</span>
              )}
              
              <div
                className={`inline-block max-w-xs px-3 py-2 rounded-lg ${
                  msg.type === 'system'
                    ? 'bg-yellow-100 text-yellow-800 mx-auto'
                    : msg.senderId === currentUserId
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-800 border border-gray-200 shadow-sm'
                }`}
              >
                <p className="text-sm">{escapeHtml(msg.message || '')}</p>
                <span className="text-xs opacity-75 mt-1 block">
                  {(() => {
                    if (!msg.timestamp) return '';
                    try {
                      // Check if it's a Firestore Timestamp (has toDate method)
                      const timestamp = msg.timestamp as any;
                      if (typeof timestamp.toDate === 'function') {
                        return timestamp.toDate().toLocaleTimeString('nl-NL', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                      }
                      // If it's already a Date object or can be converted
                      const date = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp);
                      return date.toLocaleTimeString('nl-NL', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      });
                    } catch (error) {
                      return '';
                    }
                  })()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Time Slots */}
      {timeSlots.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium mb-2">Voorgestelde Tijdsloten:</h4>
          <div className="space-y-2">
            {timeSlots.map((slot) => (
              <div
                key={slot.id}
                className={`p-3 border rounded-lg flex justify-between items-center ${
                  slot.status === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-white'
                }`}
              >
                <div>
                  <div className="font-medium">
                    {slot.startTime?.toLocaleString('nl-NL')}
                  </div>
                  <div className="text-sm text-gray-500">
                    Voorgesteld door: {slot.proposedBy === currentUserId ? 'Jij' : 'Andere gebruiker'}
                  </div>
                </div>
                
                {slot.status === 'proposed' && slot.proposedBy !== currentUserId && (
                  <button
                    onClick={() => acceptTimeSlot(slot.id)}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Accepteren
                  </button>
                )}
                
                {slot.status === 'accepted' && (
                  <span className="text-green-600 font-medium text-sm">✓ Geaccepteerd</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t pt-4">
        <div className="flex gap-2 mb-2">
          <input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type een bericht..."
            className="flex-1 border rounded-lg px-3 py-2"
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          />
          <button
            onClick={sendMessage}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Verstuur
          </button>
        </div>
        
        <button
          onClick={() => setShowTimeSlotPicker(true)}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
        >
          📅 Tijdslot Voorstellen
        </button>
      </div>

      {/* Time Slot Picker Modal */}
      {showTimeSlotPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="font-semibold mb-4">Tijdslot Voorstellen</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Datum</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Tijd</label>
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowTimeSlotPicker(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={proposeTimeSlot}
                disabled={!selectedDate || !selectedTime}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300"
              >
                Voorstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


